/**
 * FireflyConfig — 萤火虫粒子系统配置
 *
 * 关键：在暗色摄影背景上必须有足够的可见度
 */

// ─── 色彩系统 ───
export const COLORS = {
  // 主色：月光青白（占 70%）
  moonWhite: { r: 220, g: 255, b: 235 } as RGB,
  // 暖金（占 25%）
  warmGold: { r: 255, g: 240, b: 190 } as RGB,
  // 翠绿（占 5%）
  lifeGreen: { r: 180, g: 255, b: 210 } as RGB,
};

// ─── 三层景深 ───
export interface LayerConfig {
  count: number;
  coreSize: [number, number];
  glowRadius: [number, number];
  speedRange: [number, number];
  opacityRange: [number, number];
  breathRange: [number, number];
  depth: number;
}

export const LAYERS: Record<'far' | 'mid' | 'near', LayerConfig> = {
  // 远景：小而多，营造氛围
  far: {
    count: 30,
    coreSize: [1.2, 2],
    glowRadius: [10, 18],
    speedRange: [0.008, 0.025],
    opacityRange: [0.35, 0.65],
    breathRange: [5000, 9000],
    depth: 0.2,
  },
  // 中景：主要萤火虫
  mid: {
    count: 18,
    coreSize: [2, 3.5],
    glowRadius: [16, 32],
    speedRange: [0.012, 0.04],
    opacityRange: [0.5, 0.85],
    breathRange: [3500, 7000],
    depth: 0.55,
  },
  // 近景：大而亮，缓慢呼吸
  near: {
    count: 6,
    coreSize: [3, 5],
    glowRadius: [25, 50],
    speedRange: [0.005, 0.02],
    opacityRange: [0.6, 1.0],
    breathRange: [6000, 10000],
    depth: 0.85,
  },
};

// ─── 行为参数 ───
export const BEHAVIOR = {
  wanderStrength: 0.1,
  maxDriftSpeed: 0.05,

  // 呼吸场
  fieldRadius: 240,
  fieldCenterX: 0.5,
  fieldCenterY: 0.45,
  fieldBreathCycle: 8000,

  // 鼠标
  mouseRadius: 90,
  mouseForce: 0.008,

  // 生命周期
  fadeInDuration: 400,
  fadeOutOnScroll: true,
  scrollFadeStart: 0.45,
  scrollFadeEnd: 0.95,

  globalMaxOpacity: 1.0,
};

export const PERFORMANCE = {
  dpr: 2,
  fps: 60,
  pauseWhenHidden: true,
  respectReducedMotion: true,
};

interface RGB {
  r: number;
  g: number;
  b: number;
}

export function randomFireflyColor(): RGB {
  const roll = Math.random();
  if (roll < 0.7) return COLORS.moonWhite;
  if (roll < 0.95) return COLORS.warmGold;
  return COLORS.lifeGreen;
}

export function getFieldCenter(width: number, height: number): { x: number; y: number } {
  return {
    x: width * BEHAVIOR.fieldCenterX,
    y: height * BEHAVIOR.fieldCenterY,
  };
}
